import { FlagOutlined } from '@mui/icons-material';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Crisp } from 'crisp-sdk-web';
import { navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import Dialog from '../../components/dialog';
import { AppContext } from '../../context';
import environment from '../../environment';

type TReportTypes = 'offensive' | 'illegal' | 'other';

const types: Array<{ key: TReportTypes; labelKey: string }> = [
  { key: 'offensive', labelKey: 'geovelo.rides.detail.report_dialog.types.offensive' },
  { key: 'illegal', labelKey: 'geovelo.rides.detail.report_dialog.types.illegal' },
  { key: 'other', labelKey: 'geovelo.rides.detail.report_dialog.types.other' },
];

function Report({ path }: { path: string }): React.JSX.Element {
  const [dialogOpen, openDialog] = useState(false);
  const [type, setType] = useState<TReportTypes>('offensive');
  const [message, setMessage] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (typeof document !== 'undefined') {
      Crisp.configure(environment.crisp?.websiteId || '');
      Crisp.chat.hide();
    }
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      setType('offensive');
      setMessage('');
    }
  }, [dialogOpen]);

  function handleSubmit() {
    if (!currentUser || (type === 'other' && !message)) return;

    setSubmitting(true);

    try {
      Crisp.session.setSegments(['ride-report', `ride-report-${type}`], true);
      Crisp.message.send(
        'text',
        `${currentUser.pseudo} a signalé la balade ${environment.frontendUrl}${path} (raison : ${
          type === 'other'
            ? message
            : t(types.find(({ key }) => key === type)?.labelKey || '').toLowerCase()
        })`,
      );
      enqueueSnackbar(t('geovelo.rides.detail.report_dialog.feedback.success'));
      openDialog(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('geovelo.rides.detail.report_dialog.feedback.server_error'));
    }

    setSubmitting(false);
  }

  return (
    <>
      <Button
        disabled={currentUser === undefined}
        onClick={() =>
          currentUser
            ? openDialog(true)
            : navigate(`/${currentLanguage}/sign-in`, { state: { prevPath: path } })
        }
        size="small"
        startIcon={<FlagOutlined color="action" fontSize="small" />}
      >
        <Typography color="textSecondary" sx={{ textDecoration: 'underline' }}>
          {t('geovelo.rides.detail.actions.report')}
        </Typography>
      </Button>
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={(_, reason) => (!isSubmitting || reason !== 'backdropClick') && openDialog(false)}
        open={dialogOpen}
      >
        <DialogTitle>{t('geovelo.rides.detail.report_dialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography>{t('geovelo.rides.detail.report_dialog.description')}</Typography>
          <RadioGroup
            aria-label="report type"
            onChange={({ target: { value } }) => setType(value as TReportTypes)}
            value={type}
          >
            {types.map(({ key, labelKey }, index) => (
              <Fragment key={key}>
                {index > 0 && <Divider />}
                <FormControlLabel
                  control={<Radio size="small" />}
                  disabled={isSubmitting}
                  label={t(labelKey)}
                  value={key}
                />
              </Fragment>
            ))}
          </RadioGroup>
          {type === 'other' && (
            <Box flexGrow={1}>
              <TextField
                fullWidth
                multiline
                disabled={isSubmitting}
                error={type === 'other' && message.length < 10}
                label={t('geovelo.rides.detail.report_dialog.reason_label')}
                margin="dense"
                onChange={({ currentTarget: { value } }) => setMessage(value)}
                rows={3}
                slotProps={{ htmlInput: { min: 10 } }}
                value={message}
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} onClick={() => openDialog(false)} size="small">
            {t('commons.actions.cancel')}
          </Button>
          <Button
            color="primary"
            disabled={isSubmitting || (type === 'other' && message.length < 10)}
            onClick={handleSubmit}
            size="small"
            variant="contained"
          >
            {t('commons.actions.validate')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Report;
